<template>
  <Teleport to="body">
    <Transition name="ps-dialog">
      <div
        v-if="dialogState.visible"
        class="ps-dialog-backdrop"
        role="presentation"
        @click.self="backdropDismiss"
      >
        <div
          class="ps-dialog"
          :class="`ps-dialog--${dialogState.phase}`"
          :style="dialogState.width ? { maxWidth: widthValue } : null"
          role="alertdialog"
          aria-modal="true"
          :aria-labelledby="dialogState.title ? 'ps-dialog-title' : undefined"
          :aria-describedby="dialogState.message ? 'ps-dialog-message' : undefined"
        >
          <div class="ps-dialog__badge">
            <span v-if="dialogState.loading" class="ps-dialog__spinner" aria-hidden="true"></span>
            <v-icon v-else-if="dialogState.icon" :icon="dialogState.icon" size="36" />
          </div>

          <h2 v-if="dialogState.title" id="ps-dialog-title" class="ps-dialog__title">
            {{ dialogState.title }}
          </h2>

          <p v-if="dialogState.message" id="ps-dialog-message" class="ps-dialog__message">
            {{ dialogState.message }}
          </p>

          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-if="dialogState.html" class="ps-dialog__message" v-html="sanitizedHtml"></div>

          <div v-if="dialogState.showConfirm || dialogState.showCancel" class="ps-dialog__actions" :class="{ 'ps-dialog__actions--reverse': dialogState.reverseButtons }">
            <button
              v-if="dialogState.showCancel"
              type="button"
              class="ps-dialog__button ps-dialog__button--ghost"
              :style="dialogState.cancelColor ? { color: dialogState.cancelColor } : null"
              @click="closeDialog"
            >
              {{ dialogState.cancelText }}
            </button>
            <button
              v-if="dialogState.showConfirm"
              ref="confirmRef"
              type="button"
              class="ps-dialog__button ps-dialog__button--primary"
              :style="dialogState.confirmColor ? { background: dialogState.confirmColor } : null"
              @click="confirmDialog"
            >
              {{ dialogState.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { dialogState, backdropDismiss, closeDialog, confirmDialog, escapeDismiss } from '@/services/ui/dialog'

const confirmRef = ref(null)

const widthValue = computed(() => {
  const width = dialogState.width
  return typeof width === 'number' ? `${width}px` : width
})

// Only one call site passes html, and it builds the string itself. Strip tags
// that could execute rather than trusting it blindly.
const sanitizedHtml = computed(() =>
  String(dialogState.html || '')
    .replace(/<\s*(script|iframe|object|embed|link|style)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|iframe|object|embed|link|style)\b[^>]*\/?>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1="#"')
)

const onKeydown = event => {
  if (!dialogState.visible) return
  if (event.key === 'Escape') escapeDismiss()
  if (event.key === 'Enter' && dialogState.showConfirm) confirmDialog()
}

// Move focus to the primary action so the dialog is reachable by keyboard and
// announced by screen readers instead of leaving focus stranded behind it.
watch(
  () => dialogState.visible,
  async visible => {
    if (!visible) return
    await nextTick()
    confirmRef.value?.focus?.()
  }
)

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<style scoped>
.ps-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--ps-z-dialog);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ps-space-4);
  padding-bottom: calc(var(--ps-space-4) + env(safe-area-inset-bottom));
  background: rgba(10, 10, 12, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.ps-dialog {
  width: 100%;
  max-width: 340px;
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--ps-space-5) var(--ps-space-4) var(--ps-space-4);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--ps-ink);
  border: 1px solid var(--ps-ink-border);
  border-radius: var(--ps-radius-lg);
  box-shadow: var(--ps-shadow-lg);
}

.ps-dialog__badge {
  width: 68px;
  height: 68px;
  margin-bottom: var(--ps-space-3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: var(--ps-text-on-ink);
  animation: ps-dialog-pop var(--ps-duration-slow) var(--ps-ease-pop);
}

.ps-dialog--success .ps-dialog__badge {
  background: var(--ps-success-bg);
  color: var(--ps-success);
}
.ps-dialog--error .ps-dialog__badge {
  background: var(--ps-error-bg);
  color: var(--ps-error);
}
.ps-dialog--warning .ps-dialog__badge,
.ps-dialog--question .ps-dialog__badge {
  background: var(--ps-warning-bg);
  color: var(--ps-warning);
}
.ps-dialog--info .ps-dialog__badge {
  background: var(--ps-info-bg);
  color: var(--ps-info);
}
.ps-dialog--loading .ps-dialog__badge {
  background: linear-gradient(135deg, var(--ps-accent-wash-from), var(--ps-accent-wash-to));
}

.ps-dialog__spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid rgba(92, 122, 10, 0.25);
  border-top-color: var(--ps-accent-ink);
  animation: ps-dialog-spin 720ms linear infinite;
}

.ps-dialog__title {
  margin: 0 0 var(--ps-space-1);
  font-size: 19px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.2px;
  color: var(--ps-text-on-ink);
}

.ps-dialog__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  max-width: 40ch;
  color: var(--ps-text-on-ink-muted);
  overflow-wrap: anywhere;
}

.ps-dialog__actions {
  display: flex;
  gap: var(--ps-space-2);
  width: 100%;
  margin-top: var(--ps-space-4);
}

.ps-dialog__actions--reverse {
  flex-direction: row-reverse;
}

.ps-dialog__button {
  flex: 1;
  min-height: 46px;
  padding: 0 var(--ps-space-3);
  border: none;
  border-radius: var(--ps-radius-pill);
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform var(--ps-duration-fast) var(--ps-ease), box-shadow var(--ps-duration-fast) var(--ps-ease),
    background var(--ps-duration-fast) var(--ps-ease);
}

.ps-dialog__button:focus-visible {
  outline: 2px solid var(--ps-accent);
  outline-offset: 2px;
}

.ps-dialog__button--primary {
  background: linear-gradient(135deg, var(--ps-accent), var(--ps-accent-deep));
  color: #1a1a1a;
  box-shadow: var(--ps-shadow-accent);
}

.ps-dialog__button--primary:active {
  transform: scale(0.97);
}

.ps-dialog__button--ghost {
  background: rgba(255, 255, 255, 0.07);
  color: var(--ps-text-on-ink);
}

.ps-dialog__button--ghost:active {
  transform: scale(0.97);
}

@keyframes ps-dialog-pop {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes ps-dialog-spin {
  to {
    transform: rotate(360deg);
  }
}

.ps-dialog-enter-active,
.ps-dialog-leave-active {
  transition: opacity var(--ps-duration) var(--ps-ease);
}

.ps-dialog-enter-active .ps-dialog,
.ps-dialog-leave-active .ps-dialog {
  transition: transform var(--ps-duration) var(--ps-ease), opacity var(--ps-duration) var(--ps-ease);
}

.ps-dialog-enter-from,
.ps-dialog-leave-to {
  opacity: 0;
}

.ps-dialog-enter-from .ps-dialog,
.ps-dialog-leave-to .ps-dialog {
  transform: translateY(12px) scale(0.97);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ps-dialog__badge,
  .ps-dialog__spinner {
    animation-duration: 1ms;
    animation-iteration-count: 1;
  }
  .ps-dialog__spinner {
    animation: none;
    border-top-color: var(--ps-accent-ink);
  }
}
</style>
